import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { useEffect, useState } from "react";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import CrowdStrikeConnectForm from "./CrowdStrikeConnectForm";
import cs_api_key_example from "./images/cs_setup_api_key_example.png";
import cs_setup_ip_allowlist from "./images/cs_setup_ip_allowlist.png";
import cs_menu from "./images/cs_setup_menu.png";
import cs_permissions from "./images/cs_setup_permissions.png";

import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineInstruction from "../components/GuidelineInstruction";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import BreezeStaticIPsModal from "../components/BreezeStaticIPsModal";
import ProgressWithTimer from "./ProgressWithTimer";
import useStaticIPs from "../../../../api/hooks/useStaticIPs";
import { onboardingKeys } from "../../../../api/queryKeys";

// CrowdStrike console URLs for different regions
const CROWDSTRIKE_CONSOLE_URLS = [
  {
    name: "US-1",
    url: "https://falcon.crowdstrike.com/login/",
  },
  {
    name: "US-2",
    url: "https://falcon.us-2.crowdstrike.com/login/",
  },
  {
    name: "EU-1",
    url: "https://falcon.eu-1.crowdstrike.com/login/",
  },
  {
    name: "GOV",
    url: "https://api.laggar.gcw.crowdstrike.com/login/",
  },
];

const CrowdStrikeIntegration = () => {
  const [loading, setLoading] = useState(true);
  const [selectedEndpoint, setSelectedEndpoint] = useState("");
  const { staticIPs: allowlistIps } = useStaticIPs();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [missingScopes, setMissingScopes] = useState([]);
  const [isCheckingComplete, setIsCheckingComplete] = useState(false);
  const [showStaticIPsModal, setShowStaticIPsModal] = useState(false);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("crowdstrike");
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  useEffect(() => {
    setLoading(isIntegratedLoading);
  }, [isIntegratedLoading]);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerCrowdStrikeIntegration, checkCrowdStrikeReadScopes } =
    useAPI();

  const onSubmit = async (values) => {
    setLoading(true);
    Toast.info("Checking credentials and permissions please wait...");
    try {
      await registerCrowdStrikeIntegration(
        values.clientId,
        values.secret,
        values.domain
      );
      queryClient.invalidateQueries({
        queryKey: integrationKeys.all,
      });
      // only in EDR integrations, we need to invalidate the onboarding context
      queryClient.invalidateQueries({
        queryKey: onboardingKeys.all,
      });
      const missing_scopes = await checkCrowdStrikeReadScopes();
      setIsCheckingComplete(true);
      if (missing_scopes.data && missing_scopes.data.length > 0) {
        setMissingScopes(missing_scopes.data);
        setIsDialogOpen(true);
      } else {
        Toast.success("CrowdStrike Falcon is successfully integrated!");
      }
    } catch (error) {
      if (error.response) {
        Toast.error(`Something went wrong: ${error.response.data.detail}`);
      } else {
        Toast.error("An error occurred while processing the request.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  return (
    <div>
      <Box>
        <Guideline
          title={
            !isIntegrated && !loading
              ? "Once integrated, the Breeze platform will automatically read the configurations from CrowdStrike and prioritize the suggested remediations."
              : ""
          }
          image={logos.CrowdStrikeLogo}
          name={"CrowdStrike"}
          content={
            loading ? (
              <LoadingIcon size={25} />
            ) : isIntegrated && !guideMode ? (
              <Box>
                <Typography variant={"h4"}>
                  CrowdStrike Falcon is integrated!
                </Typography>
              </Box>
            ) : (
              <div>
                {/* BUTTONS TO CONTROL EXPANSION */}
                <Box mb="20px">
                  <Grid container spacing={2} direction={"row"}>
                    {guideMode && <IntegrationGuideAlert />}

                    <Grid item>
                      <SecondaryButton onClick={handleExpandAll}>
                        Expand All Screenshots
                      </SecondaryButton>
                    </Grid>
                    <Grid item>
                      <SecondaryButton onClick={handleCollapseAll}>
                        Collapse All Screenshots
                      </SecondaryButton>
                    </Grid>
                    <Grid item>
                      <SecondaryButton
                        onClick={() => setShowStaticIPsModal(true)}
                      >
                        View Breeze Static IPs
                      </SecondaryButton>
                    </Grid>
                  </Grid>
                </Box>

                {/* SETUP INSTRUCTIONS */}
                <GuidelineStepByStep>
                  <GuidelineStep title="Login to CrowdStrike Falcon dashboard">
                    <Typography variant="body1">
                      Select your organization's Falcon console endpoint:
                    </Typography>
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <FormControl fullWidth sx={{ maxWidth: 400 }}>
                        <InputLabel>Select Endpoint</InputLabel>
                        <Select
                          value={selectedEndpoint}
                          label="Select Endpoint"
                          onChange={(e) => setSelectedEndpoint(e.target.value)}
                        >
                          {CROWDSTRIKE_CONSOLE_URLS.map((console) => (
                            <MenuItem key={console.name} value={console.url}>
                              {console.name} -{" "}
                              {console.url
                                .replace("https://", "")
                                .replace("/login/", "")}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    {selectedEndpoint && (
                      <PrimaryButton
                        onClick={() => {
                          window.open(selectedEndpoint, "_blank");
                        }}
                        sx={{ mt: 1 }}
                      >
                        Login to Selected Endpoint
                      </PrimaryButton>
                    )}
                  </GuidelineStep>
                  <GuidelineStep title="Add Breeze to the allowlist IP - Optional">
                    <Typography variant="subtitle1">
                      If your organization is not using an IP allowlist, skip to
                      step 3.
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      Go to:{" "}
                      <strong>
                        Host setup and management > Falcon users > IP Allowlist
                        Management
                      </strong>
                    </Typography>

                    <Typography
                      variant="subtitle1"
                      sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                    >
                      IP Addresses to Allowlist:
                    </Typography>
                    {allowlistIps.map((ip, index) => (
                      <Box
                        key={index}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{
                          margin: 0,
                          padding: "4px 0",
                          marginLeft: "20px",
                        }}
                      >
                        <Typography variant="body1">{ip}</Typography>
                        <CustomCopyButton
                          copyTitle={"IP Address"}
                          copyText={ip}
                        />
                      </Box>
                    ))}

                    <Typography variant="body1" sx={{ mt: 2 }}>
                      You can add the IPs to a new group or an existing group.
                    </Typography>
                    <Typography variant="body1">
                      Make sure to give Access type <strong>"API"</strong>
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: cs_setup_ip_allowlist,
                          altext: "IP allowlist management page",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </GuidelineStep>
                  <GuidelineStep title="Navigate to API clients and keys">
                    <Typography variant="body1">
                      In the left-hand menu, click on{" "}
                      <strong>Support and resources</strong> and then click on{" "}
                      <strong>API clients and keys</strong>
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        { file: cs_menu, altext: "API clients and keys menu" },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </GuidelineStep>
                  <GuidelineStep title="Create a new API key">
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      API Client Name:
                    </Typography>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{
                        margin: 0,
                        padding: "4px 0",
                        marginLeft: "20px",
                        mb: 2,
                      }}
                    >
                      <Typography variant="body1">Breeze Platform</Typography>
                      <CustomCopyButton
                        copyTitle={"API Client Name"}
                        copyText={"Breeze Platform"}
                      />
                    </Box>

                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Permission Configuration:
                    </Typography>
                    <Typography variant="body1" sx={{ ml: 2 }}>
                      • Select the <strong>Read</strong> checkbox for all scopes
                      except for <strong>'Installation tokens'</strong> &{" "}
                      <strong>'Real Time response'</strong>
                    </Typography>
                    <Typography variant="body1" sx={{ ml: 2 }}>
                      • Review all scopes to verify they are checked, ensuring
                      successful integration
                    </Typography>
                    <Typography variant="body1" sx={{ ml: 2 }}>
                      • No Write scopes are required
                    </Typography>
                    <Typography variant="body1" sx={{ ml: 2, mt: 1 }}>
                      • Finally, click <strong>Create</strong> to generate the
                      API key
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: cs_permissions,
                          altext: "API key permissions",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </GuidelineStep>
                  <GuidelineStep title="Copy the Client ID and Secret values here">
                    <Typography variant="body1">
                      This is the final page once the API client is created.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: cs_api_key_example,
                          altext: "Post API creation",
                          note: "NOTE: the values shown in the screenshot are for illustration purposes only",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                    <Box width={700}>
                      <CrowdStrikeConnectForm
                        onSubmit={onSubmit}
                        guideMode={guideMode}
                      />
                    </Box>
                  </GuidelineStep>
                </GuidelineStepByStep>
              </div>
            )
          }
        />
      </Box>
      {loading && (
        <Box
          sx={{
            width: "80%",
          }}
        >
          <ProgressWithTimer
            duration={60} // estimate time duration in seconds
            isCheckingComplete={isCheckingComplete}
          />
        </Box>
      )}
      <Dialog open={isDialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Missing Scopes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The following scopes are missing:
            {missingScopes.map((scope, index) => (
              <React.Fragment key={index}>
                <br />
                {index + 1}. <strong>{scope}</strong>
              </React.Fragment>
            ))}
          </DialogContentText>
          <DialogContentText>
            Make sure to copy the missing scopes and grant them soon.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <BreezeStaticIPsModal
        open={showStaticIPsModal}
        onClose={() => setShowStaticIPsModal(false)}
      />
    </div>
  );
};

export default CrowdStrikeIntegration;
